import React, { useEffect, useState, useRef } from 'react';
import { userToken, executeLaravelAPI, showToastMessage } from '../../Utils';
import ReactPaginate from 'react-paginate';

const itemsPerPageOptions = [20, 50, 100, 500, 1000];

const Leads = () => {
  const pageDetails = { pagetitle: 'Leads', pageslug: 'leads' };

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1); // 1-based page
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sort, setSort] = useState('id');
  const [order, setOrder] = useState('asc');
  const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageOptions[0]);
  const [searchQuery, setSearchQuery] = useState('');

  const searchInputRef = useRef(null);
  const inputRef = useRef(null);

  // refs to persist between renders
  const isApiCallInProgressRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const lastRequestIdRef = useRef(0); // prevents race conditions

  useEffect(() => {
    document.title = pageDetails.pagetitle;
  }, []);

  // fetch leads whenever page, itemsPerPage, sort, order change (only when not actively searching)
  useEffect(() => {
    if (!searchQuery.trim()) {
      fetchLeads(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, sort, order]);

  const fetchProjectName = async (id) => {
    if (!id) return '';
    try {
      const response = await executeLaravelAPI(`project/view/${id}`, '', 'GET', userToken);
      if (response && response.status === 200) return response.data.name || '';
    } catch (err) { /* ignore */ }
    return '';
  };

  const fetchDeveloperName = async (id) => {
    if (!id) return '';
    try {
      const response = await executeLaravelAPI(`developer/${id}`, '', 'GET', userToken);
      if (response && response.status === 200) return response.data.name || '';
    } catch (err) { /* ignore */ }
    return '';
  };

  const fetchLeads = async (page = 1) => {
    setLoading(true);
    setError('');

    const qs = `?page=${page}&per_page=${itemsPerPage}&sort=${encodeURIComponent(sort)}&order=${encodeURIComponent(order)}`;

    try {
      const response = await executeLaravelAPI(`enquiries${qs}`, '', 'GET', userToken);

      if (response && response.status === 200) {
        const data = response.data;
        const leadsData = data.data || [];

        // enrich in parallel
        const enhancedLeads = await Promise.all(
          leadsData.map(async (lead) => {
            const [projectName, developerName] = await Promise.all([
              fetchProjectName(lead.project_id),
              fetchDeveloperName(lead.developer_id),
            ]);
            return { ...lead, projectName, developerName };
          })
        );

        setLeads(enhancedLeads);
        setTotalPages(data.last_page ?? 1);
        setTotalItems(data.total ?? enhancedLeads.length);
      } else {
        setError('Error fetching leads');
      }
    } catch (err) {
      console.error('fetchLeads error', err);
      setError('Error fetching leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (field === sort) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('asc');
    }
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected + 1);
  };

  const handleItemsPerPageChange = (e) => {
    const selectedValue = parseInt(e.target.value, 10);
    setItemsPerPage(selectedValue);
    setCurrentPage(1); // reset to page 1
  };

  // SEARCH: improved but using same API endpoints you already had
  // - numeric => call enquiry/{id}
  // - non-numeric => fetch current page of enquiries (same enquiries endpoint) and filter client-side
  async function executeApiCall(inputValue) {
    // keep original API requests unchanged
    lastRequestIdRef.current += 1;
    const requestId = lastRequestIdRef.current;

    if (isApiCallInProgressRef.current) {
      // allow cancelling older request's update by bumping requestId
    }
    isApiCallInProgressRef.current = true;
    setLoading(true);
    setError('');

    try {
      if (!inputValue) {
        showToastMessage('Input is empty', 'top-right', 'warning');
        // refetch list
        await fetchLeads(1);
        return;
      }

      // If input looks like a numeric ID, call the single-enquiry endpoint (keeps your existing request)
      const numericId = /^\d+$/.test(inputValue);

      if (numericId) {
        const response = await executeLaravelAPI(`enquiry/${inputValue}`, '', 'GET', userToken);

        // if another request started after this one, ignore result
        if (requestId !== lastRequestIdRef.current) return;

        if (response && response.status === 200) {
          // your original code expected response.data.leads (but single show might return object)
          // handle both shapes:
          let leadsData = [];
          if (Array.isArray(response.data.leads)) {
            leadsData = response.data.leads;
          } else if (response.data && response.data.id) {
            leadsData = [response.data];
          } else if (response.data && response.data.data) {
            // some endpoints wrap data
            leadsData = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
          }

          const enhanced = await Promise.all(
            leadsData.map(async (lead) => {
              const [projectName, developerName] = await Promise.all([
                fetchProjectName(lead.project_id),
                fetchDeveloperName(lead.developer_id),
              ]);
              return { ...lead, projectName, developerName };
            })
          );

          setLeads(enhanced);
          setTotalItems(enhanced.length);
          setTotalPages(1);
          setCurrentPage(1);
          if (!enhanced.length) showToastMessage('Lead Not Found', 'top-right', 'warning');
        } else {
          showToastMessage('Lead Not Found', 'top-right', 'warning');
          setLeads([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      } else {
        // NON-NUMERIC: keep using the same 'enquiries' API (no new route)
        // Fetch current page of enquiries and filter locally by id/name/email/phone
        const qs = `?page=1&per_page=${itemsPerPage}&sort=${encodeURIComponent(sort)}&order=${encodeURIComponent(order)}`;
        const response = await executeLaravelAPI(`enquiries${qs}`, '', 'GET', userToken);

        if (requestId !== lastRequestIdRef.current) return;

        if (response && response.status === 200) {
          const data = response.data;
          const leadsData = data.data || [];

          // local filter (case-insensitive)
          const q = inputValue.toLowerCase();
          const filtered = leadsData.filter((lead) => {
            if (!lead) return false;
            const idMatch = String(lead.id || '').includes(q);
            const nameMatch = String(lead.name || '').toLowerCase().includes(q);
            const emailMatch = String(lead.email || '').toLowerCase().includes(q);
            const phoneMatch = String(lead.phone || '').toLowerCase().includes(q);
            return idMatch || nameMatch || emailMatch || phoneMatch;
          });

          const enhanced = await Promise.all(
            filtered.map(async (lead) => {
              const [projectName, developerName] = await Promise.all([
                fetchProjectName(lead.project_id),
                fetchDeveloperName(lead.developer_id),
              ]);
              return { ...lead, projectName, developerName };
            })
          );

          setLeads(enhanced);
          setTotalItems(enhanced.length);
          setTotalPages(1);
          setCurrentPage(1);

          if (!enhanced.length) showToastMessage('No leads found on this page', 'top-right', 'warning');
        } else {
          setLeads([]);
          setTotalItems(0);
          setTotalPages(1);
          showToastMessage('No leads found', 'top-right', 'warning');
        }
      }
    } catch (err) {
      console.error('executeApiCall error', err);
      showToastMessage('Something went wrong', 'top-right', 'error');
      setError(`Error fetching data: ${err.message || err}`);
    } finally {
      isApiCallInProgressRef.current = false;
      setLoading(false);
    }
  }

  const handleInputChange = (e) => {
    const raw = e.target.value;
    setSearchQuery(raw); // show raw input value

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      // bump request id so older requests won't apply
      lastRequestIdRef.current += 1;
      const trimmed = raw.trim();
      if (trimmed === '') {
        // cleared -> restore paginated list
        lastRequestIdRef.current += 1; // cancel any inflight
        fetchLeads(1);
      } else {
        executeApiCall(trimmed);
      }
    }, 700);
  };

  const clearSearch = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    lastRequestIdRef.current += 1; // cancel pending results
    setSearchQuery('');
    setLeads([]);
    setTotalItems(0);
    setTotalPages(1);
    fetchLeads(1);
  };

  // helper for "Showing x-y of z"
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <>
      <section className="content">
        <div className="row">
          <div className='col-lg-12'>
            <div className='position-relative' ref={searchInputRef}>
              <div className='position-realtive d-flex'>
                <input
                  type="search"
                  className='form-control search-input shadow-none border-none'
                  placeholder="Search by Lead ID eg. 1001"
                  value={searchQuery}
                  onChange={handleInputChange}
                  ref={inputRef}
                  autoComplete='off'
                  style={{ background: "url('https://api.iconify.design/ion/search-outline.svg') no-repeat 10px center / 24px" }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="btn btn-sm ms-2"
                    style={{ height: '38px' }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-5">
            <div className="box">
              <div className="box-header with-border">
                <h3 className="box-title">Leads</h3>
              </div>
              <div className="box-body">
                <div className="box-body table-container mt-4 hr-scroll-style">
                  <table className="table table-resposive table-hover dashboard-table shadow-sm mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '10px' }}>Id</th>
                        <th onClick={() => handleSort('name')}>Name</th>
                        <th onClick={() => handleSort('email')}>Email</th>
                        <th onClick={() => handleSort('phone')}>Phone</th>
                        <th onClick={() => handleSort('project')}>Project</th>
                        <th onClick={() => handleSort('developer')}>Developer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={7}><div className='loading'><span className="loadingspan">Loading...</span><span className="custom-loader"></span></div></td></tr>
                      ) : (
                        leads.length === 0 ? (
                          <tr><td colSpan={7}>No leads found</td></tr>
                        ) : (
                          leads.map((lead, index) => (
                            <tr key={`${lead.id}-${index}`}>
                              <td className='sticky-column'>{lead.id}</td>
                              <td className='sticky-column column-snd'>{lead.name}</td>
                              <td>{lead.email}</td>
                              <td>{lead.phone}</td>
                              <td>{lead.projectName}</td>
                              <td className="text-capitalize">{lead.developerName}</td>
                            </tr>
                          ))
                        )
                      )}
                    </tbody>
                    <tfoot></tfoot>
                  </table>
                </div>

                <div className='row mt-5 align-items-center'>
                  <div className='col-lg-4'>
                    <p className='graylight mb-0'>Showing {startItem}-{endItem} of {totalItems} items</p>
                  </div>
                  <div className='col-lg-8 d-flex justify-content-end align-items-center'>
                    <div className='me-3'>
                      <span className='graylight'>Results per page </span>
                      <select className='showPerPage' value={itemsPerPage} onChange={handleItemsPerPageChange}>
                        {itemsPerPageOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>

                    <ReactPaginate
                      pageCount={totalPages}
                      forcePage={currentPage - 1}
                      onPageChange={handlePageClick}
                      containerClassName={'pagination justify-content-end mb-0'}
                      pageClassName={'page-item'}
                      pageLinkClassName={'page-link'}
                      activeClassName={'active'}
                      previousClassName={'page-item'}
                      previousLinkClassName={'page-link'}
                      nextClassName={'page-item'}
                      nextLinkClassName={'page-link'}
                      breakClassName={'page-item'}
                      breakLinkClassName={'page-link'}
                      disableInitialCallback={true}
                    />
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Leads;
